import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { User, Mail, Building, Phone, } from 'lucide-react';
import { toast } from 'sonner';
import { useCreateProviderCustomer } from '@/hooks/provider/useProviderCustomers';
import { normalizeLanguageCode } from '@/lib/i18n/normalize-language';

const createCustomerSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  company: z.string().optional(),
  phone: z.string().optional(),
  // timezone removed per UI update
  // timezone: z.string().default('UTC'),
  role: z.enum(['individual', 'team_owner']),
  teamName: z.string().optional(),
  initialProduct: z.string().optional(),
  sendInvite: z.boolean().default(true),
});

type CreateCustomerFormData = z.infer<typeof createCustomerSchema>;

interface CreateCustomerDialogProps {
  open: boolean;
  onOpenChange: (_open: boolean) => void;
  products: Array<{ id: string; name: string }>;
}

export function CreateCustomerDialog({ open, onOpenChange, products }: CreateCustomerDialogProps) {
  const { t, i18n } = useTranslation();
  const createCustomer = useCreateProviderCustomer();
  const language = normalizeLanguageCode(i18n.language);

  const form = useForm<CreateCustomerFormData>({
    resolver: zodResolver(createCustomerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      phone: '',
      // timezone: 'UTC',
      role: 'individual',
      teamName: '',
      initialProduct: 'none',
      sendInvite: true,
    },
  });

  const role = form.watch('role');

  async function onSubmit(data: CreateCustomerFormData) {
    try {
      await createCustomer.mutateAsync({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        language,
        company: data.company,
        phone: data.phone,
        // timezone removed from payload
        role: data.role,
        teamName: data.teamName,
        sendInvite: data.sendInvite,
      });
      toast.success(t('customers.create.success', 'Customer created successfully'));
      onOpenChange(false);
      form.reset();
    } catch (error) {
      const message = error instanceof Error
        ? error.message
        : t('customers.create.error', 'Failed to create customer');
      toast.error(message);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('customers.create.title')}</DialogTitle>
          <DialogDescription>
            {t('customers.create.description')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('customers.create.fields.firstName')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input {...field} className="pl-9" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('customers.create.fields.lastName')}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('customers.create.fields.email')}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input {...field} type="email" className="pl-9" />
                      </div>
                    </FormControl>
                    <FormDescription>
                      {t('customers.create.fields.emailDescription')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* company/phone moved below account type and shown conditionally */}

              <Separator />

              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('customers.create.fields.accountType')}</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="individual">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            <span>{t('customers.create.fields.individual')}</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="team_owner">
                          <div className="flex items-center gap-2">
                            <Building className="h-4 w-4" />
                            <span>{t('customers.create.fields.teamOwner')}</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {t('customers.create.fields.accountTypeDescription')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {role === 'team_owner' && (
                <FormField
                  control={form.control}
                  name="teamName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('customers.create.fields.teamName')}</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder={t('customers.create.fields.teamNamePlaceholder')} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {role === 'team_owner' && (
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('customers.create.fields.company')}</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Building className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input {...field} className="pl-9" />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('customers.create.fields.phone')}</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input {...field} className="pl-9" />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              <FormField
                control={form.control}
                name="initialProduct"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('customers.create.fields.initialProduct')}</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('customers.create.fields.selectProduct')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">
                          {t('customers.create.fields.noProduct')}
                        </SelectItem>
                        {products.map((product) => (
                          <SelectItem key={product.id} value={product.id}>
                            {product.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {t('customers.create.fields.initialProductDescription')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={createCustomer.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={createCustomer.isPending}>
                {createCustomer.isPending ? t('common.creating') : t('customers.create.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default CreateCustomerDialog;
